import React, { Component } from 'react';
import Header from 'components/Header/Header';
import { getUnifiedCurrentUser, musicEnginePost, musicEngineGet } from '../../api/api';
import Style from 'pages/updateProfile/updateProfileStyles.module.css';
import Navigation from 'navigation/navigation';
import PageSpinner from 'components/Spinner/PageSpinner';
import { withRouter } from 'react-router-dom';
import placeHolderImg from 'assets/dummy-profile.png';
import { storage } from 'utils/firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { logEventPagesViewed } from 'utils/analytics';

class UpdateProfilePage extends Component {
    constructor(props) {
        super(props);
        this.state = {
            user: null,
            name: '',
            country: '',
            gender: '',
            birthDay: '',
            birthMonth: '',
            birthYear: '',
            profilePicture: null,
            profilePictureUrl: '',
            loading: false,
            errorMessage: '',
            activeTab: 0,
            isProfileCompletion: false, // Flag to determine if this is profile completion
            genders: ['M','F'],
            countries: [],
            initializing: true
        };
        this.onKeyDown = this.onKeyDown.bind(this);
        this.fileInputRef = React.createRef();
    }

    componentDidMount() {
        // Track page view (non-blocking)
        logEventPagesViewed(null, window.location.href, 'updateprofile_page', 'UpdateProfile').catch(() => {});
        
        // Check if this is profile completion or update profile
        const isProfileCompletion = this.props.location.state?.isProfileCompletion || false;
        this.setState({ isProfileCompletion });
        this.fetchUserData();
        this.fetchCountries();
        document.addEventListener('keydown', this.onKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.onKeyDown);
    }

    async fetchCountries() {
        try {
            const response = await musicEngineGet('/countries');
            const countries = response.countries || response || [];
            this.setState({ countries });
        } catch (error) {
            this.setState({ countries: [] });
        }
    }

    async fetchUserData() {
        try {
            // Add timestamp to prevent caching
            const { user } = await getUnifiedCurrentUser();
            if (user) {
                const genderRaw = user.gender || '';
                const gender = (genderRaw === 'Male' ? 'M' : genderRaw === 'Female' ? 'F' : genderRaw);
                const country = user.country || user.country_code || '';
                // birth may be 'YYYY-MM-DD' or 'MM/DD/YYYY'
                let birthDay = '', birthMonth = '', birthYear = '';
                const rawBirth = user.birth || user.birthDate || user.birth_date || user.dob || user.date_of_birth || user.birthday || '';
                if (rawBirth && /\d{4}-\d{2}-\d{2}/.test(rawBirth)) {
                    const [y, m, d] = rawBirth.split('-');
                    birthYear = y; birthMonth = m; birthDay = d;
                } else if (rawBirth && /\d{1,2}\/\d{1,2}\/\d{4}/.test(rawBirth)) {
                    const [m, d, y] = rawBirth.split('/');
                    birthYear = y; birthMonth = m.padStart(2,'0'); birthDay = d.padStart(2,'0');
                }
                // If all required fields are present and this is profile completion flow, redirect
                const hasBasics = !!(((user.displayName || user.name) && gender && country));
                const hasDob = !!(birthDay && birthMonth && birthYear);
                if (this.state.isProfileCompletion && hasBasics && hasDob) {
                    this.setState({ initializing: false });
                    this.props.history.replace('/podcast');
                    return;
                }
                this.setState({
                    user,
                    name: user.displayName || user.name || '',
                    gender,
                    country,
                    birthDay,
                    birthMonth,
                    birthYear,
                    profilePictureUrl: user.photoURL || user.artwork_url || '',
                    initializing: false
                });
            } else {
                this.setState({ initializing: false });
            }
        } catch (err) {
            this.setState({ initializing: false });
        }
    }

    onKeyDown(e) {
        // Ignore when typing in form fields
        const target = e && e.target;
        const tag = target && target.tagName ? target.tagName.toLowerCase() : '';
        const isFormField = tag === 'input' || tag === 'textarea' || tag === 'select' || (target && target.isContentEditable);
        if (isFormField) {
            return;
        }
        if (!e.key) {
            return;
        }
        const { history } = this.props;
        const { activeTab, isProfileCompletion } = this.state;

        switch (e.key) {
            case 'SoftRight':
                // Don't allow going back for profile completion
                if (!isProfileCompletion) {
                    history.goBack();
                }
                break;
            case 'ArrowDown':
                if (activeTab < 4) {
                    this.setState(prevState => {
                        setTimeout(() => { this.scrollToElement(prevState.activeTab + 1); });
                        return { activeTab: prevState.activeTab + 1 };
                    });
                }
                break;
            case 'ArrowUp':
                if (activeTab > 0) {
                    this.setState(prevState => {
                        setTimeout(() => { this.scrollToElement(prevState.activeTab - 1); });
                        return { activeTab: prevState.activeTab - 1 };
                    });
                }
                break;
            case 'Enter':
            case 'SoftLeft':
                if (activeTab === 0) {
                    this.fileInputRef.current.click();
                } else if (activeTab === 4) {
                    this.handleSubmit();
                }
                break;
            case 'Backspace': {
                // Don't navigate back when typing in inputs
                const target = e && e.target;
                const tag = target && target.tagName ? target.tagName.toLowerCase() : '';
                const isFormField = tag === 'input' || tag === 'textarea' || tag === 'select' || (target && target.isContentEditable);
                if (!isProfileCompletion && !isFormField) {
                    history.goBack();
                }
                break;
            }
            default:
                break;
        }
    }

    scrollToElement(id) {
        setTimeout(() => {
            const element = document.getElementById(`element-${id}`);
            if (element) {
                window.scroll({
                    top: element.offsetTop,
                    left: 0,
                    behavior: 'smooth'
                });
            }
        });
    }

    handleNameChange = (e) => {
        this.setState({ name: e.target.value });
    }

    handleGenderChange = (gender) => {
        this.setState({ gender });
    }

    handleCountryChange = (e) => {
        this.setState({ country: e.target.value });
    }

    handleDOBChange = (field, value) => {
        const currentYear = new Date().getFullYear();
        let validatedValue = value;

        if (field === 'birthDay') {
            const day = parseInt(value);
            if (day < 1) validatedValue = '1';
            else if (day > 31) validatedValue = '31';
        } else if (field === 'birthMonth') {
            const month = parseInt(value);
            if (month < 1) validatedValue = '1';
            else if (month > 12) validatedValue = '12';
        } else if (field === 'birthYear') {
            const year = parseInt(value);
            if (year < 1900) validatedValue = '1900';
            else if (year > currentYear) validatedValue = currentYear.toString();
        }

        this.setState({ [field]: validatedValue });
    }

    handleProfilePictureChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            this.setState({ profilePicture: file });
            const reader = new FileReader();
            reader.onload = (e) => {
                this.setState({ profilePictureUrl: e.target.result });
            };
            reader.readAsDataURL(file);
        }
    }

    uploadProfilePicture = async (file) => {
        if (!file) return null;
        
        try {
            const user = this.state.user;
            const fileName = `profile-pictures/${user.uid}/${Date.now()}-${file.name}`;
            const storageRef = ref(storage, fileName);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            throw new Error('Failed to upload profile picture');
        }
    }



    handleSubmit = async () => {
        const { name, country, gender, birthDay, birthMonth, birthYear, profilePicture, isProfileCompletion } = this.state;
        
        if (!name.trim()) {
            this.setState({ errorMessage: 'Name is required' });
            return;
        }

        // For profile completion, require minimal ME fields
        if (isProfileCompletion) {
            if (!gender) {
                this.setState({ errorMessage: 'Please select your gender' });
                return;
            }
            if (!birthDay || !birthMonth || !birthYear) {
                this.setState({ errorMessage: 'Please enter your full date of birth' });
                return;
            }
            if (!country) {
                this.setState({ errorMessage: 'Please enter your country' });
                return;
            }
        }

        this.setState({ loading: true, errorMessage: '' });

        try {
            let artworkFile = null;
            
            // Upload profile picture if selected
            if (profilePicture) {
                artworkFile = profilePicture;
            }

            const form = new FormData();
            form.append('name', name.trim());
            if (country) form.append('country', country);
            if (gender) form.append('gender', gender);
            const mm = String(birthMonth || '').padStart(2, '0');
            const dd = String(birthDay || '').padStart(2, '0');
            const yyyy = String(birthYear || '');
            if (mm && dd && yyyy) {
                form.append('birth', `${mm}/${dd}/${yyyy}`);
            }
            if (artworkFile) form.append('artwork', artworkFile);

            const response = await musicEnginePost('/auth/user/settings/profile', form);
            
            // Update local state
            this.setState(prevState => ({
                user: { ...prevState.user, name, gender },
                loading: false
            }));

            // Add a small delay to ensure backend has processed the update
            // Mark profile completed locally so pages stop redirecting back here
            localStorage.setItem('me_profile_completed', '1');
            setTimeout(() => {
                if (isProfileCompletion) {
                    this.props.history.replace('/podcast');
                } else {
                    this.props.history.replace('/settings');
                }
            }, 300);
        } catch (error) {
            this.setState({ 
                loading: false, 
                errorMessage: (error && (error.message || error)) || 'Failed to update profile' 
            });
        }
    }

    render() {
        const { 
            name, 
            ageGroup, 
            gender, 
            profilePictureUrl, 
            loading, 
            errorMessage, 
            activeTab,
            ageGroups,
            genders,
            isProfileCompletion,
            initializing
        } = this.state;

        if (initializing) {
            return <PageSpinner />;
        }

        return (
            <>
                <Header title={isProfileCompletion ? "Complete Your Profile" : "Update Profile"} />
                <div className={Style.container}>
                    <div className={Style.content}>
                        {/* Profile Picture Section */}
                        <div 
                            id="element-0"
                            className={`${Style.section} ${activeTab === 0 ? Style.active : ''}`}
                            onClick={() => this.fileInputRef.current.click()}
                        >
                            <div className={Style.profilePictureContainer}>
                                <img 
                                    src={profilePictureUrl || placeHolderImg} 
                                    alt="Profile" 
                                    className={Style.profilePicture}
                                />
                                <div className={Style.uploadOverlay}>
                                    <span>Click to upload photo</span>
                                </div>
                            </div>
                            <input
                                ref={this.fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={this.handleProfilePictureChange}
                                style={{ display: 'none' }}
                            />
                        </div>

                        {/* Name Section */}
                        <div 
                            id="element-1"
                            className={`${Style.section} ${activeTab === 1 ? Style.active : ''}`}
                        >
                            <label className={Style.label}>Name</label>
                            <input
                                type="text"
                                value={name}
                                onChange={this.handleNameChange}
                                className={Style.input}
                                placeholder="Enter your name"
                            />
                        </div>

                        {/* Gender (MusicEngine expects 'M' or 'F') */}
                        <div 
                            id="element-3"
                            className={`${Style.section} ${activeTab === 3 ? Style.active : ''}`}
                        >
                            <label className={Style.label}>Gender</label>
                            <div className={Style.optionsContainer}>
                                {genders.map((gen) => (
                                    <div
                                        key={gen}
                                        className={`${Style.option} ${gender === gen ? Style.selected : ''}`}
                                        onClick={() => this.handleGenderChange(gen)}
                                    >
                                        {gen}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Country */}
                        <div 
                            id="element-2"
                            className={`${Style.section} ${activeTab === 2 ? Style.active : ''}`}
                        >
                            <label className={Style.label}>Country</label>
                            <select
                                value={this.state.country}
                                onChange={this.handleCountryChange}
                                className={Style.input}
                            >
                                <option value="">Select a country</option>
                                {this.state.countries.map((country) => (
                                    <option key={country.code || country.iso_code} value={country.code || country.iso_code}>
                                        {country.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Date of Birth: DD / MM / YYYY */}
                        <div 
                            id="element-5"
                            className={`${Style.section} ${activeTab === 5 ? Style.active : ''}`}
                        >
                            <label className={Style.label}>Date of Birth</label>
                            <div className={Style.dobRow}>
                                <input
                                    type="number"
                                    value={this.state.birthDay}
                                    onChange={(e) => this.handleDOBChange('birthDay', e.target.value)}
                                    onBlur={(e) => this.handleDOBChange('birthDay', e.target.value)}
                                    className={Style.input}
                                    placeholder="DD"
                                    min="1"
                                    max="31"
                                />
                                <input
                                    type="number"
                                    value={this.state.birthMonth}
                                    onChange={(e) => this.handleDOBChange('birthMonth', e.target.value)}
                                    onBlur={(e) => this.handleDOBChange('birthMonth', e.target.value)}
                                    className={Style.input}
                                    placeholder="MM"
                                    min="1"
                                    max="12"
                                />
                                <input
                                    type="number"
                                    value={this.state.birthYear}
                                    onChange={(e) => this.handleDOBChange('birthYear', e.target.value)}
                                    onBlur={(e) => this.handleDOBChange('birthYear', e.target.value)}
                                    className={Style.input}
                                    placeholder="YYYY"
                                    min="1900"
                                    max={new Date().getFullYear()}
                                />
                            </div>
                        </div>


                        {/* Error Message */}
                        {errorMessage && (
                            <div className={Style.errorMessage}>
                                {errorMessage}
                            </div>
                        )}

                                                 {/* Submit Button */}
                         <div 
                             id="element-4"
                             className={`${Style.section} ${activeTab === 4 ? Style.active : ''}`}
                         >
                                                         <button
                                 className={`${Style.submitButton} ${loading ? Style.loading : ''}`}
                                 onClick={this.handleSubmit}
                                 disabled={loading}
                             >
                                 {loading ? (isProfileCompletion ? 'Completing Profile...' : 'Updating...') : (isProfileCompletion ? 'Complete Profile' : 'Update Profile')}
                             </button>
                        </div>
                    </div>
                </div>
                <Navigation active="Profile" />
            </>
        );
    }
}

export default withRouter(UpdateProfilePage); 